import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";

const API = import.meta.env.VITE_API_URL || "https://pizzasteve-api.m-2396.workers.dev";
const IG = "https://ig.me/m/mr.pizzastevefinds";

function cloudImg(url: string, width = 400) {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_auto/`);
}

interface Product {
  id: string; name: string; size?: string; price?: number; priceLabel?: string;
  status: string; emoji?: string; tag?: string; imageUrl?: string;
  images?: string[]; condition?: string; description?: string;
}

export const Route = createFileRoute("/product/")({ component: ProductPage });

function ProductPage() {
  const { id } = Route.useParams();
  const cart = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  function showToast(message: string) {
    setToastMessage(message);
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }

  const isInCart = product ? cart.items.some((item) => item.id === product.id) : false;

  function handleAddToCart() {
    if (product) {
      cart.add({
        id: product.id,
        name: product.name,
        price: product.price,
        priceLabel: product.priceLabel,
        imageUrl: product.imageUrl,
        size: product.size,
        emoji: product.emoji,
      });
      showToast(`Added ${product.name} to cart`);
    }
  }

  useEffect(() => {
    fetch(`${API}/api/products/${id}`).then(r => r.json()).then(d => { setProduct(d); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then(r => r.json())
      .then(d => setAllProducts(d))
      .catch(() => {});
  }, []);

  const recommendations = allProducts
    .filter(p => p.id !== id && p.status === "available")
    .sort((a, b) => {
      if (product && a.tag === product.tag && b.tag !== product.tag) return -1;
      if (product && b.tag === product.tag && a.tag !== product.tag) return 1;
      return 0;
    })
    .slice(0, 4);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="w-10 h-10 border-2 border-zinc-800 border-t-orange-400 rounded-full animate-spin" /></div>;
  if (!product) return <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center"><p>item not found 😔 probably sold already</p></div>;

  const allImages = [product.imageUrl, ...(product.images || [])].filter(Boolean) as string[];

  const igMessage = encodeURIComponent(`Hi! I want to reserve: ${product.name}${product.size ? ` (Size: ${product.size})` : ""}`);
  const igLink = `${IG}?text=${igMessage}`;

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-4 py-12">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-zinc-900 border border-zinc-800 text-white text-[10px] uppercase tracking-[0.2em] font-black px-6 py-3.5 shadow-2xl animate-fade-slide-up rounded-full flex items-center gap-2.5 backdrop-blur-md">
          <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
          {toastMessage}
        </div>
      )}
      <div className="max-w-5xl mx-auto">
        <Link to="/shop" className="text-zinc-500 hover:text-zinc-100 active:scale-95 text-sm tracking-widest mb-8 inline-block transition-colors">
          BACK TO SHOP
        </Link>

        <div className="grid md:grid-cols-2 gap-10 mt-4">
          {/* Images */}
          <div>
            <div className="aspect-square bg-zinc-900 rounded-2xl overflow-hidden mb-3">
              {allImages.length > 0
                ? <img src={allImages[activeImg]} alt={product.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-8xl">{product.emoji}</div>}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors active:scale-95 ${activeImg === i ? "border-zinc-200" : "border-zinc-700"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col">
            {product.tag && <span className="text-zinc-100 text-xs font-bold tracking-widest mb-2">{product.tag}</span>}
            <h1 className="text-2xl font-black leading-tight mb-4">{product.name}</h1>

            <div className="flex items-center gap-4 mb-6">
              <span className={`text-3xl font-black ${product.status === "sold" ? "text-zinc-600" : "text-zinc-100"}`}>
                {product.status === "sold" ? (
                  "SOLD"
                ) : product.price ? (
                  <>
                    {product.price}{" "}
                    <span className="text-[0.6em] font-sans font-bold tracking-wider text-muted-foreground ml-1">EGP</span>
                  </>
                ) : (
                  product.priceLabel || "DM for price"
                )}
              </span>
            </div>

            <div className="space-y-3 mb-8">
              {product.size && (
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm w-20">Size</span>
                  <span className="bg-zinc-800 text-white text-sm font-bold px-3 py-1 rounded">{product.size}</span>
                </div>
              )}
              {product.condition && (
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm w-20">Condition</span>
                  <span className="text-white text-sm">{product.condition}</span>
                </div>
              )}
            </div>

            {product.description && (
              <p className="text-zinc-400 text-sm leading-relaxed mb-8">{product.description}</p>
            )}

            {product.status === "available" ? (
              <div className="space-y-3">
                {isInCart ? (
                  <Link to="/cart"
                    className="w-full bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white font-black text-center py-4 rounded-xl tracking-widest transition-colors text-sm block">
                    ADDED! VIEW CART →
                  </Link>
                ) : (
                  <button onClick={handleAddToCart}
                    className="w-full bg-primary hover:bg-secondary active:scale-95 text-primary-foreground font-black text-center py-4 rounded-xl tracking-widest transition-colors text-sm">
                    ADD TO CART
                  </button>
                )}
                <button
                  onClick={() => {
                    if (product) {
                      if (!isInCart) {
                        handleAddToCart();
                      }
                      navigate({ to: "/checkout" });
                    }
                  }}
                  className="w-full bg-white hover:bg-zinc-200 active:scale-95 text-zinc-950 font-black text-center py-4 rounded-xl tracking-widest transition-colors text-sm"
                >
                  BUY IT NOW
                </button>
              </div>
            ) : (
              <div className="w-full bg-zinc-800 text-zinc-500 font-black text-center py-4 rounded-xl tracking-widest text-sm">
                GONE 💀 someone was faster
              </div>
            )}
          </div>
        </div>

        {/* You may also like */}
        {recommendations.length > 0 && (
          <div className="mt-20 border-t border-zinc-800 pt-16">
            <h2 className="text-xs uppercase tracking-[0.35em] text-zinc-400 mb-8 font-black">
              YOU MAY ALSO LIKE
            </h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {recommendations.map((p) => {
                const inCart = cart.items.some(item => item.id === p.id);
                return (
                  <Link
                    key={p.id}
                    to="/product/$id"
                    params={{ id: p.id }}
                    className="group relative bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-zinc-700 flex flex-col active:scale-95"
                  >
                    <div className="relative aspect-square overflow-hidden bg-zinc-950">
                      {p.imageUrl ? (
                        <img
                          src={cloudImg(p.imageUrl, 400)}
                          alt={p.name}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-4xl">{p.emoji}</div>
                      )}
                      <span className="absolute left-3 top-3 text-[9px] uppercase tracking-widest text-zinc-100 font-bold bg-zinc-800/80 px-2 py-0.5 rounded">
                        {p.tag}
                      </span>
                    </div>

                    <div className="p-4 flex flex-col flex-1">
                      <p className="line-clamp-1 text-[11px] font-bold uppercase tracking-wider text-zinc-100">
                        {p.name}
                      </p>
                      <div className="mt-3 flex items-center justify-between gap-2 mt-auto">
                        <span className="text-[12px] font-black text-white">
                          {p.price ? `${p.price} EGP` : p.priceLabel || "Price on Request"}
                        </span>
                        <div className="flex items-center gap-2">
                          {p.size && (
                            <span className="text-[9px] font-bold uppercase bg-zinc-800 text-white px-2.5 py-0.5 rounded">
                              sz {p.size}
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!inCart) {
                                cart.add({
                                  id: p.id,
                                  name: p.name,
                                  price: p.price,
                                  priceLabel: p.priceLabel,
                                  imageUrl: p.imageUrl,
                                  size: p.size,
                                  emoji: p.emoji,
                                });
                                showToast(`Added ${p.name} to cart`);
                              }
                            }}
                            className={`px-2.5 py-1 text-[9px] font-bold uppercase rounded-lg transition-all duration-200 border ${
                              inCart
                                ? "border-zinc-850 text-zinc-500 bg-zinc-900 cursor-default"
                                : "border-zinc-700 hover:border-zinc-200 text-zinc-300 hover:text-white hover:bg-zinc-800"
                            }`}
                          >
                            {inCart ? "In Cart" : "+ Add"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

